import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useShadowHost, useTopLevelPortal } from '@homerunner-next/widget-core/runtime'
import { subscribeSbpAnchor } from './anchorBus'
import { useWidget } from '../../../../../providers/widget'
import { useExplorerCss, ELEVATED_TOKEN_SCOPE } from '../../../hooks/useExplorerCss'
import { useExplorer } from '../../../providers'
import { ExploreWidgetConfig, VisitorFilter } from '../../../config'
import { useNameFinder } from './useNameFinder'
import { useNearby, NEARBY_COPY } from './useNearby'
import type { NearbyMatch } from '../../../nearby/centroids'
import { usePropertyNavigate } from './propertyNav'
import { useWhereSections, useAppliedWhereLabel, ownedWhereFilterKeys, useForeignDestinationValues, WhereOption, WhereSection } from './whereSources'
import DestinationPanel from '../../../destinations/DestinationPanel'
import { buildDestinationTabs, filterDestinationTabs, flattenTabEntries, limitTabEntries, destinationOwnedKeys, resolveDestinationAppliedLabel, type DestinationEntry } from '../../../destinations/tabs'
import { useSbpLabels } from './labels'
import { useSbpCompact } from './compact'
import FieldClear from './FieldClear'

/** Sheet-row subtitle per section (module parity: the mobile suggestion rows
 *  are two-line, name over a dimension word — Shortcode::get_location_terms
 *  subtitles). Keyed by WhereSection.key (the curation key). */
const SECTION_SUB: Record<string, string> = {
  locations: 'Region',
  cities: 'City',
  groups: 'Group',
  states: 'State',
  countries: 'Country',
}

type Section = WhereSection

/**
 * Shadow-DOM-safe containment for document-level outside-close listeners.
 * In production the widget lives in an open shadow root, so a document
 * listener sees `e.target` RETARGETED to the shadow host — `contains()`
 * alone reads every inside interaction as outside. composedPath() crosses
 * open shadow boundaries and carries the real origin (exported for tests).
 */
export const isEventInsideRoot = (root: Node, e: Event): boolean =>
  typeof e.composedPath === 'function'
    ? e.composedPath().includes(root)
    : root.contains(e.target as Node)

/**
 * Pro Where field (#853, toolkit: SBP destination combobox). A real input with
 * an anchored suggestion listbox — browse mode (curated destinations, grouped
 * Regions/Cities in zone mode) before typing, client-side filtered suggestions
 * after minQueryLength, plus the find-by-name property section when enabled
 * and its endpoint is live (dark until central#2885).
 *
 * Selection is SINGLE-pick (SBP parity: picking a destination commits one
 * scalar param): it writes that dimension immediately and, in zone mode,
 * clears the sibling dimension. Typed-but-unselected text NEVER mutates
 * filters — closing without a pick reverts the input to the applied selection.
 */
// `sheet`/`sheetActive`/`onSheetActivate` (Eli r5): inside the mobile sheet's
// accordion the field renders a collapsed stage header (label + applied
// summary) and, while active, the question + input + listbox expanded inline —
// the module's Where stage. The listbox is held open by the ACTIVE STAGE, not
// by input focus, so the curated browse list shows without raising a keyboard.

export default function ProWhereField({ inline = false, sheet = false, sheetActive = false, onSheetActivate, onDestinationPick }: { inline?: boolean; sheet?: boolean; sheetActive?: boolean; onSheetActivate?: () => void; onDestinationPick?: () => void }) {
  const { options, feedId, widgetId } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter } = useExplorer()
  const navigateToProperty = usePropertyNavigate()
  const sb = options.searchbar
  const labels = useSbpLabels()
  // #1700: the compact bar is desktop-only; the phone sheet (sheet/inline) stays labelled.
  const compactBar = useSbpCompact()
  const compact = compactBar.compact && !sheet && !inline

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const source = options.whereFieldSource ?? 'city'
  const sections = useWhereSections()

  // ── Destination Filter port (#1072 D1, toolkit destination-filter) ────────
  // When the operator has built a destination tree and switched the module on,
  // the Where popup becomes the tabbed destination browser. The field around
  // it is untouched: same input, same clear pill, same popover positioning,
  // same mobile sheet. The tree is derived in the dashboard app and read here
  // straight from settings, so a visitor never pays for derivation.
  const dest = options.destinations
  const destinationTabs = useMemo(
    () => buildDestinationTabs(options.destinationTree, dest?.groupOrder),
    [options.destinationTree, dest?.groupOrder],
  )
  const destinationMode = Boolean(dest?.enabled) && destinationTabs.length > 0
  // The field's own input IS the popup's search — a second box inside the
  // panel would give one field two competing queries.
  // Gate on the same minimum the legacy search uses: filtering from the
  // first character would blank the whole panel below the threshold (the
  // empty state is typedEnough-gated), making the popup vanish silently on
  // one unmatched keystroke (codex cold pass).
  const destinationSearching = debouncedQuery.trim().length >= sb.autocomplete.minQueryLength
  const visibleDestinationTabs = useMemo(
    () => {
      const filtered = filterDestinationTabs(destinationTabs, destinationSearching ? debouncedQuery : '')
      // maxResults is documented as "Maximum destination suggestions shown"
      // and the plain list budget-caps with it — but only while a query is
      // active, since browse mode is the curated universe in both looks.
      return destinationSearching ? limitTabEntries(filtered, sb.autocomplete.maxResults) : filtered
    },
    [destinationTabs, debouncedQuery, destinationSearching, sb.autocomplete.maxResults],
  )
  // The active tab lives HERE, not in the panel, because the keyboard cursor
  // (activeIndex / flatItems below) has to walk the rows of whichever tab is
  // showing. Searching can remove the chosen tab; fall back to the first that
  // still has rows rather than an empty panel.
  const [destinationTabKey, setDestinationTabKey] = useState<string | undefined>(undefined)
  const activeDestinationTab = visibleDestinationTabs.find((t) => t.key === destinationTabKey) ?? visibleDestinationTabs[0]

  // Accordion (toolkit parity): islands start COLLAPSED, so opening the field
  // paints one row per island instead of every side in the tree. While a query
  // is active everything expands — a search that hid its own matches behind a
  // chevron would read as "no results". Keyed by island term id.
  const [expandedIslands, setExpandedIslands] = useState<ReadonlySet<number>>(() => new Set())
  const isDestinationExpanded = useMemo(
    () => (entry: DestinationEntry) => destinationSearching || expandedIslands.has(entry.id),
    [destinationSearching, expandedIslands],
  )
  const toggleDestinationExpanded = (entry: DestinationEntry) => {
    setExpandedIslands((prev) => {
      const next = new Set(prev)
      if (next.has(entry.id)) next.delete(entry.id)
      else next.add(entry.id)
      return next
    })
  }
  // Same gate the panel paints under, so flatItems[activeIndex] is the row the
  // visitor can actually see.
  const destinationEntries = useMemo(
    () => flattenTabEntries(activeDestinationTab, isDestinationExpanded),
    [activeDestinationTab, isDestinationExpanded],
  )

  // #925 → #935 item 3: suggestions-only mobile sheet. NET-NEW (the module's
  // show_where removes the whole field, and its input is UNCONDITIONAL — no
  // module threshold exists) — hides the text input inside the SHEET only and
  // keeps the browse-mode suggestion list permanently visible there, so
  // visitors pick from the curated destinations without a keyboard. 'auto'
  // (the r6 default) shows the input only when the destination list outgrows
  // the threshold — Eli: "flip default to show-search if the number of
  // locations and cities is larger than X". Forced 'show'/'hide' (the legacy
  // boolean's meanings) still win outright. The desktop bar instance never
  // reads any of this.
  const destinationCount = destinationMode
    ? destinationTabs.reduce((n, t) => n + flattenTabEntries(t).length, 0)
    : sections.reduce((n, s) => n + s.options.length, 0)
  const searchInputMode = sb.mobileSheet.showSearchInput
  const suggestionsOnly = inline && (
    searchInputMode === 'hide'
    || (searchInputMode === 'auto' && destinationCount <= sb.mobileSheet.searchThreshold)
  )

  // Applied-selection display value (resolved from the option universe), plus
  // a RAW-filter applied check over the source's owned keys: a stored filter
  // whose term is curated out / missing from meta must still read as applied
  // and stay clearable, even when no label can be resolved for it.
  const sectionAppliedLabel = useAppliedWhereLabel(sections)
  // In destination mode the popup can write any of the three taxonomy
  // dimensions, so the field owns them for applied-state and clearing —
  // otherwise a pick made on one tab could not be cleared from another. But it
  // owns them only where the value is the POPUP's: `visitorFilterDefaults` and
  // QuickChips write the same three keys, and claiming those made the field
  // paint "Selected destination" over an operator scope on first load and let
  // its × wipe a live chip (#1246 review). destinationOwnedKeys does that cut.
  //
  // The field's own source keys ride along regardless: with a group/state/
  // country source, a pre-destination-mode filter on that dimension would
  // otherwise survive invisibly and intersect every pick (codex cold pass).
  const foreignDestinationValues = useForeignDestinationValues()
  const ownedDestinationKeys = useMemo(
    () => destinationOwnedKeys(
      (key) => state.visitorFilters[key] as string | undefined,
      (key) => foreignDestinationValues[key],
    ),
    [state.visitorFilters, foreignDestinationValues],
  )
  // Destination picks write tags/locations/cities ids that the source's
  // sections cannot resolve (a city-source field has no tags section), so
  // read the applied value back through the tree instead — the field must
  // say "Kauai", not "Selected destination".
  const destinationAppliedLabel = useMemo(
    // Shared rule (also drives the bar summaries via
    // useDestinationAwareWhereLabel) so field and summaries cannot drift.
    () => resolveDestinationAppliedLabel(
      destinationTabs,
      (key) => state.visitorFilters[key] as string | undefined,
      ownedDestinationKeys,
    ),
    [destinationTabs, state.visitorFilters, ownedDestinationKeys],
  )
  const appliedLabel = destinationMode ? destinationAppliedLabel : sectionAppliedLabel
  const ownedKeys: Array<keyof VisitorFilter> = destinationMode
    ? [...new Set<keyof VisitorFilter>([...ownedDestinationKeys, ...ownedWhereFilterKeys(source)])]
    : ownedWhereFilterKeys(source)
  const hasApplied = ownedKeys.some((k) => Boolean(state.visitorFilters[k] as string | undefined))
  const appliedDisplay = appliedLabel || (hasApplied ? 'Selected destination' : '')

  // Debounce the typed query (toolkit: debounce_ms after the last keystroke).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), sb.autocomplete.debounceMs)
    return () => clearTimeout(t)
  }, [query, sb.autocomplete.debounceMs])

  const typedEnough = debouncedQuery.trim().length >= sb.autocomplete.minQueryLength
  const filteredSections = useMemo(() => {
    if (!debouncedQuery.trim()) return sections // browse mode: full curated universe
    if (!typedEnough) return []
    const q = debouncedQuery.trim().toLowerCase()
    let budget = sb.autocomplete.maxResults
    const out: Section[] = []
    for (const section of sections) {
      if (budget <= 0) break
      const hits = section.options.filter((o) => o.name.toLowerCase().includes(q)).slice(0, budget)
      if (hits.length) {
        out.push({ ...section, options: hits })
        budget -= hits.length
      }
    }
    return out
  }, [sections, debouncedQuery, typedEnough, sb.autocomplete.maxResults])

  // Find-by-name (dark until central#2885 responds). The config leaves are
  // passed individually so the settings→behavior data flow is explicit (and
  // statically traceable by the dead-setting sentinel).
  const nameFinder = useNameFinder(feedId, {
    enabled: sb.nameFinder.enabled,
    showAllBeforeTyping: sb.nameFinder.showAllBeforeTyping,
    serverLookupThreshold: sb.nameFinder.serverLookupThreshold,
  }, open)
  useEffect(() => {
    if (open && sb.nameFinder.enabled) nameFinder.search(debouncedQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, open, nameFinder.available])
  const nameMatches = sb.nameFinder.enabled && nameFinder.available
    ? nameFinder.matches.slice(0, sb.autocomplete.maxResults)
    : []

  /**
   * Nearby (#861, toolkit: `enable_nearby`). Visible in BROWSE mode as the
   * first suggestion row. Browse mode is the toolkit's own rule — the input
   * focused with FEWER THAN `minQueryLength` characters, not strictly an empty
   * one (searchbar-pro.js renderBrowseMode) — so a half-typed query that has
   * not reached the threshold still shows the row, exactly as the module does.
   * Hidden with no index: `centroids` is empty until an
   * operator builds one, and a row that can only ever answer "nothing near
   * you" is worse than no row (the useNameFinder discipline).
   *
   * Not offered in the mobile sheet: the toolkit's mobile Where stage has no
   * Nearby row either, and its stage is a PHP-rendered term list.
   */
  const nearbyCentroids = sb.nearby.centroids
  const nearbyVisible = sb.nearby.enabled && nearbyCentroids.length > 0 && !destinationMode && !typedEnough && !sheet
  const applyNearby = useCallback((match: NearbyMatch) => {
    // Identical to a hand-pick: the resolved term is written through the same
    // single-select path, so clearing, sibling-clearing in zone mode and the
    // WHEN hand-off all behave exactly as they do for a tapped destination.
    const section = sections.find((sec) => sec.filterKey === match.centroid.filterKey)
    if (section) {
      selectDestination(section, { id: match.centroid.id, name: match.centroid.name })
      return
    }
    // The centroid's dimension is real but its term was curated out of the
    // rendered sections. Write it anyway through the owned-key path — the
    // alternative is telling a visitor standing in that city that nothing is
    // near them.
    const patch: Partial<VisitorFilter> = {}
    for (const key of ownedKeys) patch[key] = undefined
    patch[match.centroid.filterKey] = String(match.centroid.id)
    setFilter(patch)
    setQuery('')
    setDebouncedQuery('')
    setOpen(false)
    inputRef.current?.blur()
    onDestinationPick?.()
  }, [sections, ownedKeys])
  const nearby = useNearby(nearbyCentroids, ownedKeys, applyNearby)
  // A failure the visitor already read must not greet them on re-open.
  useEffect(() => { if (!open) nearby.reset() }, [open, nearby.reset])

  // Flat list for keyboard navigation: destinations first, then properties.
  // In destination mode the destinations are the VISIBLE panel rows, in the
  // panel's own render order — never the hidden section universe, or Enter
  // would apply a filter the visitor never saw.
  const flatItems = useMemo(() => {
    const items: Array<
      | { kind: 'dest'; section: Section; option: WhereOption }
      | { kind: 'destination'; entry: DestinationEntry }
      | { kind: 'property'; name: string; slug: string }
      | { kind: 'nearby' }
    > = []
    // Nearby leads, matching the toolkit's row order, so arrow-down from the
    // input reaches it first.
    if (nearbyVisible) items.push({ kind: 'nearby' })
    if (destinationMode) for (const entry of destinationEntries) items.push({ kind: 'destination', entry })
    else for (const section of filteredSections) for (const option of section.options) items.push({ kind: 'dest', section, option })
    for (const m of nameMatches) items.push({ kind: 'property', name: m.name, slug: m.slug })
    return items
  }, [nearbyVisible, destinationMode, destinationEntries, filteredSections, nameMatches])

  // activeDestinationTab?.key: switching to an equal-sized tab must not keep
  // the previous tab's row highlighted (codex cold pass).
  useEffect(() => setActiveIndex(-1), [flatItems.length, open, activeDestinationTab?.key])

  const selectDestination = (section: Section, option: WhereOption) => {
    const patch: Partial<VisitorFilter> = { [section.filterKey]: String(option.id) }
    if (source === 'zone') {
      // Single-pick across the combined picker: selecting in one dimension
      // clears the other so the two CSVs never intersect a pick (SBP scalar
      // commit parity).
      const sibling = section.filterKey === 'locations' ? 'cities' : 'locations'
      patch[sibling] = undefined
    }
    setFilter(patch)
    setQuery('')
    setDebouncedQuery('')
    setOpen(false)
    inputRef.current?.blur()
    // Chain hand-off (#923): a destination pick advances the hero bar into
    // the WHEN popover — the module has done this since before #469; the
    // auto_advance setting now makes the whole chain opt-out. Desktop only:
    // the inline sheet instance gets no callback from ProFilterBar.
    onDestinationPick?.()
  }

  /**
   * Destination pick. The Where field is single-select, so a pick REPLACES any
   * previous destination rather than intersecting with it: choosing a city
   * after an island must not silently AND the two into an empty result set.
   * Scoped to the three keys the popup writes.
   */
  const selectDestinationNode = (entry: DestinationEntry) => {
    const patch: Partial<VisitorFilter> = {}
    // Clear every owned key (the three destination dimensions PLUS the
    // field's own source keys), then set the pick.
    for (const key of ownedKeys) patch[key] = undefined
    patch[entry.filterKey] = String(entry.id)
    setFilter(patch)
    setQuery('')
    setDebouncedQuery('')
    setOpen(false)
    inputRef.current?.blur()
    onDestinationPick?.()
  }

  const clearSelection = () => {
    // Owned keys, NOT rendered sections: sections drop dimensions with empty
    // curated option lists, which would leave their applied values behind.
    const patch: Partial<VisitorFilter> = {}
    for (const key of ownedKeys) patch[key] = undefined as never
    setFilter(patch)
    setQuery('')
    setDebouncedQuery('')
    // Deliberately does NOT refocus the input. The toolkit's clearField('where')
    // clears and CLOSES (searchbar-pro.js:3462-3468); refocusing here would
    // re-fire onFocus → setOpen(true) and pop the listbox open on every clear,
    // which is exactly what the clear button's pointerdown guard exists to
    // prevent on the other three fields.
  }

  const selectItem = (item: (typeof flatItems)[number]) => {
    // Nearby does NOT close the listbox: the answer arrives asynchronously
    // (permission prompt, then a fix), and closing first would leave the
    // visitor with no idea whether anything is happening.
    if (item.kind === 'nearby') nearby.resolve()
    else if (item.kind === 'dest') selectDestination(item.section, item.option)
    else if (item.kind === 'destination') selectDestinationNode(item.entry)
    else navigateToProperty(item.slug)
  }

  // Outside-MOUSEDOWN close (toolkit parity: click waits for re-renders that
  // can detach the target and misread containment). Containment must use
  // composedPath(): the widget mounts in an open shadow root in production,
  // where document-level events are retargeted to the shadow host — a bare
  // contains(e.target) reads every inside touch as outside and closes the
  // listbox before the option's mousedown handler can fire.
  //
  // #880: the INLINE (mobile sheet) listbox is `position: static` — in flow —
  // so collapsing it lifts every stage below by its own height (324px on the
  // measured repro). Closing on the pointerdown of a tap yanks the tapped
  // stage button out from under the thumb before the tap resolves, so the
  // browser never synthesizes a click and the first tap on When/Who/Filters is
  // swallowed. Inline therefore DECIDES on pointerdown — containment is still
  // read at the un-reflowed moment, keeping the shadow-DOM fix above intact —
  // and ACTS on the gesture's own click.
  //
  // It has to be the click specifically. Measured event order for one tap in
  // Chromium (Pixel 5 emulation): pointerdown, touchstart, pointerup,
  // touchend, [any task queued at pointerup runs HERE], mousedown, mouseup,
  // click. The compatibility mouse events trail a setTimeout(0) armed at
  // pointerup, so deferring to the gesture *end* still reflows the sheet
  // before the click's hit test and loses the tap just the same. The click is
  // the last event of the gesture, and it reaches this listener (composed, so
  // it crosses the elevated portal's open shadow root) only after React has
  // already run the tapped stage's own handler.
  //
  // The anchored desktop listbox is absolutely positioned, collapses without
  // moving anything, and keeps closing immediately.
  useEffect(() => {
    if (!open) return
    let closePending = false
    const close = () => {
      setOpen(false)
      setQuery('')
      setDebouncedQuery('')
    }
    const onDown = (e: MouseEvent | TouchEvent) => {
      const root = rootRef.current
      if (!root) return
      // The portaled listbox (#943) lives at document.body, outside the
      // field's DOM — an inside-the-listbox press must not read as outside.
      const portaledListbox = listboxRef.current
      if (portaledListbox && isEventInsideRoot(portaledListbox, e)) return
      // Every down re-decides, so the latch always belongs to the most recent
      // gesture. Without this an arm could outlive the gesture that set it:
      // a non-primary press outside (middle-click, or right-click dismissed
      // with Escape) fires mousedown but never a click, leaving the latch
      // armed — and the visitor's next click INSIDE the field, to fix a typo,
      // would then collapse the suggestions they were reading.
      closePending = false
      if (isEventInsideRoot(root, e)) return
      if (!inline) {
        close()
        return
      }
      closePending = true
    }
    // click resolves a tap; pointercancel/touchcancel resolve a gesture the
    // browser turned into a scroll, which never produces one.
    const resolveClose = () => {
      if (!closePending) return
      closePending = false
      close()
    }
    const resolveEvents = ['click', 'pointercancel', 'touchcancel']
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    if (inline) for (const type of resolveEvents) document.addEventListener(type, resolveClose)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      if (inline) for (const type of resolveEvents) document.removeEventListener(type, resolveClose)
    }
  }, [open, inline])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true)
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!flatItems.length) return
      const delta = e.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((prev) => (prev + delta + flatItems.length) % flatItems.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIndex >= 0 && flatItems[activeIndex]) selectItem(flatItems[activeIndex])
    } else if (e.key === 'Escape') {
      // Sheet mode has no popup to close (#965): the suggestions are the
      // stage's own content (module: permanent "Suggested destinations" rows,
      // search-bar.php:1111-1140) and `listboxVisible` reads `sheetActive`,
      // not `open`. Escape there belongs to the sheet itself — the module's
      // mobile Where input has no Escape handler (searchbar-pro.js:5540-5543)
      // and its document-level Escape closes the modal (js:5641). The Radix
      // Dialog reproduces that via its own document-CAPTURE keydown listener
      // (use-escape-keydown), which runs BEFORE this bubble-phase React
      // handler — the sheet is already closing when this branch drops the
      // typed query and the focus, the only things that are this field's.
      if (!sheet) setOpen(false)
      setQuery('')
      setDebouncedQuery('')
      inputRef.current?.blur()
    }
  }

  // SBP-2 (#922): the module's Where is a delegated-click surface — the whole
  // field box, label strip included, activates the input, not just the
  // input's own hit area (origin/main search-bar.php:402-445; tracker
  // walkthrough defect (a), lab 8/7). mousedown + preventDefault keeps an
  // already-focused input focused instead of blur/refocus flicker. Desktop
  // only: the inline sheet's tap choreography (#880) must not gain a handler
  // that runs at pointer time.
  const onFieldMouseDown = (e: React.MouseEvent) => {
    const t = e.target as HTMLElement
    if (t === inputRef.current) return
    if (t.closest('.hfse-sbp-listbox') || t.closest('.hfse-sbp-field-clear')) return
    e.preventDefault()
    inputRef.current?.focus()
    setOpen(true)
  }

  // The panel numbers its own rows 0..n-1 in flatItems order; the shared
  // Properties section below continues from there in either look.
  let flatIndex = destinationMode ? destinationEntries.length - 1 : -1
  // aria-activedescendant needs a stable per-row id; both instances of the
  // field can be mounted at once, so it carries the same widget/role suffix
  // the input id does.
  const optionId = (index: number) => `hfse-sbp-where-opt-${widgetId}${inline ? '-inline' : ''}-${index}`
  // flatItems is mode-aware (panel rows or section options, plus property
  // matches), so one expression covers both looks. Declared above `inputRow`
  // because the input's aria-activedescendant reads it.
  const listboxVisible = (open || suggestionsOnly || (sheet && sheetActive)) && (flatItems.length > 0 || typedEnough)
  const inputRow = (
    // Sheet dress (#935 item 3): the module's mobile input is a bordered wrap
    // with a leading magnifier and a "Search destinations" placeholder
    // (search-bar.php:1111-1122) — the desktop combobox keeps its own shape.
    <div className={`hfse-sbp-where-input-row${sheet ? ' hfse-sbp-where-input-row-sheet' : ''}`} style={suggestionsOnly ? { display: 'none' } : undefined}>
      {sheet && (
        <span className="hfse-sbp-where-input-icon" aria-hidden>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
        </span>
      )}
      <input
        id={`hfse-sbp-where-${widgetId}${inline ? '-inline' : ''}`}
        ref={inputRef}
        type="text"
        className="hfse-sbp-where-input"
        role="combobox"
        // The sheet's list stays on screen without focus, so its expanded
        // state is the list's visibility, not the focus-driven `open` (#965).
        aria-expanded={sheet ? listboxVisible : open}
        aria-autocomplete="list"
        aria-activedescendant={listboxVisible && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        autoComplete="off"
        aria-label={sheet ? labels.whereLabel : undefined}
        placeholder={sheet ? labels.searchDestinations : (appliedDisplay || (compact ? compactBar.placeholderWhere : sb.placeholder))}
        value={open ? query : appliedDisplay}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onKeyDown={onKeyDown}
      />
    </div>
  )
  // Host-level stacking, round 2 (#943, supersedes #940 for the plain
  // anchored case): the #940 host-lift (z-99990 inline on the outermost
  // shadow host) only competes inside the host's own stacking context — any
  // page ancestor that creates one (transform/opacity/filter, standard in
  // hero and animated sections) traps the lift, and a DOM-later positioned
  // hero paints over the whole widget (Eli screenshot 8/8; reproduced on the
  // lab bench hostile zoo, #hostile=transform,hero). The anchored desktop
  // listbox therefore renders through the widget-core elevated portal
  // (z-99999 host on document.body — escapes every ancestor context), fixed-
  // positioned and tracked to the field rect. The sheet rides its elevated
  // Dialog; the inline and suggestionsOnly variants are in-place surfaces
  // that must stay in-tree, so they keep the #940 lift below — as do
  // shadow-less mounts (plain-DOM dev, no ShadowHostProvider), where the
  // elevated portal can never produce a target and portaling would render
  // the listbox nowhere.
  const widgetShadowHost = useShadowHost()
  const whereListboxPortaled = !sheet && !inline && !suggestionsOnly && !!widgetShadowHost
  const explorerCssString = useExplorerCss()
  const listboxPortalTarget = useTopLevelPortal({
    cssString: explorerCssString,
    enabled: whereListboxPortaled && listboxVisible,
  })
  const listboxRef = useRef<HTMLDivElement | null>(null)
  const [listboxAnchor, setListboxAnchor] = useState<{ top: number; left: number } | null>(null)
  useEffect(() => {
    if (!whereListboxPortaled || !listboxVisible) {
      setListboxAnchor(null)
      return
    }
    const update = () => {
      const r = rootRef.current?.getBoundingClientRect()
      // A zero-size rect means the field is display:none — the <933px media
      // flip while open (tablet rotation swaps in the mobile trigger), or any
      // hidden ancestor. Tracking it would pin the body-portaled listbox at
      // (0, 12) over whatever replaced the field; close instead.
      if (r && r.width === 0 && r.height === 0) {
        setOpen(false)
        return
      }
      // 12px gap restates the anchored rule (explorer.css .hfse-sbp-listbox
      // top: calc(100% + 12px)) in viewport coordinates.
      if (r) setListboxAnchor({ top: Math.round(r.bottom + 12), left: Math.round(r.left) })
    }
    update()
    // Capture-phase scroll: any scroll container between the field and the
    // viewport moves the anchor, not just the document scroller.
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    // Sticky pin/unpin moves the field after this scroll's update already
    // ran (#1220 item 1); the bar publishes once its sticky DOM commits.
    const unsubscribe = subscribeSbpAnchor(update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
      unsubscribe()
    }
  }, [whereListboxPortaled, listboxVisible])

  useEffect(() => {
    if (!listboxVisible || sheet || whereListboxPortaled) return
    let node: Node | null = rootRef.current
    let host: HTMLElement | null = null
    while (node) {
      const root = node.getRootNode()
      if (root instanceof ShadowRoot) {
        host = root.host as HTMLElement
        node = host
      } else break
    }
    if (!host) return
    const prev = { z: host.style.zIndex, pos: host.style.position }
    host.style.zIndex = '99990'
    // '' guards jsdom, which reports no computed default for position
    const computedPos = getComputedStyle(host).position
    if (!computedPos || computedPos === 'static') host.style.position = 'relative'
    return () => {
      host.style.zIndex = prev.z
      host.style.position = prev.pos
    }
  }, [listboxVisible, sheet, whereListboxPortaled])
  const listboxNode = listboxVisible && (
        <div
          className="hfse-sbp-listbox"
          // Destination mode moves the listbox INSIDE the panel: a listbox may
          // only own options and groups, and the panel's tablist is neither
          // (#1246 review). The container is then just the popover shell.
          role={destinationMode ? 'presentation' : 'listbox'}
          ref={listboxRef}
          style={whereListboxPortaled && listboxAnchor
            ? { position: 'fixed', top: listboxAnchor.top, left: listboxAnchor.left }
            : undefined}
        >
          {destinationMode ? (
            <DestinationPanel
              title={dest?.popupTitle ?? ''}
              tabs={visibleDestinationTabs}
              activeKey={activeDestinationTab?.key}
              onTabChange={setDestinationTabKey}
              activeIndex={activeIndex}
              showCounts={dest?.showCounts ?? true}
              onSelect={selectDestinationNode}
              isExpanded={isDestinationExpanded}
              onToggleExpand={toggleDestinationExpanded}
              optionId={optionId}
            />
          ) : (<>
          {nearbyVisible && (() => {
            flatIndex++
            const idx = flatIndex
            return (
              <button
                key="hfse-sbp-nearby"
                type="button"
                role="option"
                id={optionId(idx)}
                aria-selected={idx === activeIndex}
                className={`hfse-sbp-listbox-item hfse-sbp-listbox-item-nearby ${idx === activeIndex ? 'hfse-sbp-listbox-item-active' : ''}`}
                // aria-busy over a live region: the subtitle IS the status, and
                // a row that announces itself twice (name + separate status)
                // reads worse than one whose accessible name updates.
                aria-busy={nearby.status === 'locating'}
                onMouseDown={(e) => {
                  e.preventDefault()
                  nearby.resolve()
                }}
              >
                <span className="hfse-sbp-listbox-item-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" /></svg>
                </span>
                <span className="hfse-sbp-listbox-item-text">
                  <span className="hfse-sbp-listbox-item-name">Nearby</span>
                  <span className="hfse-sbp-listbox-item-sub">{NEARBY_COPY[nearby.status]}</span>
                </span>
              </button>
            )
          })()}
          {filteredSections.map((section) => (
            <div key={section.key} className="hfse-sbp-listbox-section">
              {/* Sheet mode replaces the per-section headings with the single
                  "Suggested destinations" header (module parity) — each row's
                  subtitle names its dimension instead. */}
              {!sheet && (sections.length > 1 || nameMatches.length > 0) && !options.hideZoneGroups && (
                <div className="hfse-sbp-listbox-heading">{section.label}</div>
              )}
              {section.options.map((option) => {
                flatIndex++
                const idx = flatIndex
                return (
                  <button
                    key={`${section.key}-${option.id}`}
                    type="button"
                    role="option"
                    id={optionId(idx)}
                    aria-selected={idx === activeIndex}
                    className={`hfse-sbp-listbox-item ${idx === activeIndex ? 'hfse-sbp-listbox-item-active' : ''}`}
                    // mousedown (not click) so selection commits before input blur.
                    onMouseDown={(e) => {
                      e.preventDefault()
                      selectDestination(section, option)
                    }}
                  >
                    <span className="hfse-sbp-listbox-item-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                    </span>
                    {sheet ? (
                      // Module suggestion rows are two-line: name over a
                      // dimension subtitle (#935 item 3 dress).
                      <span className="hfse-sbp-listbox-item-text">
                        <span className="hfse-sbp-listbox-item-name">{option.name}</span>
                        <span className="hfse-sbp-listbox-item-sub">{SECTION_SUB[section.key] ?? section.label}</span>
                      </span>
                    ) : option.name}
                  </button>
                )
              })}
            </div>
          ))}
          </>)}
          {/* Shared by both looks: the property name-finder is an operator
              setting in its own right and must not vanish when the
              destination popup is on (the toolkit's popup search includes
              homes too), and the empty state has to exist in both. */}
          {nameMatches.length > 0 && (
            <div
              className="hfse-sbp-listbox-section"
              // In destination mode the outer container is no longer the
              // listbox (the panel owns that), so these options need their own.
              role={destinationMode ? 'listbox' : undefined}
              aria-label={destinationMode ? 'Properties' : undefined}
            >
              <div className="hfse-sbp-listbox-heading">Properties</div>
              {nameMatches.map((m) => {
                flatIndex++
                const idx = flatIndex
                return (
                  <button
                    key={m.slug}
                    type="button"
                    role="option"
                    id={optionId(idx)}
                    aria-selected={idx === activeIndex}
                    className={`hfse-sbp-listbox-item ${idx === activeIndex ? 'hfse-sbp-listbox-item-active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      navigateToProperty(m.slug)
                    }}
                  >
                    <span className="hfse-sbp-listbox-item-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l10 9h-3v9h-6v-6H11v6H5v-9H2l10-9z" /></svg>
                    </span>
                    {m.name}
                  </button>
                )
              })}
            </div>
          )}
          {flatItems.length === 0 && typedEnough && (
            <div className="hfse-sbp-listbox-empty">No matches found.</div>
          )}
        </div>
  )
  // Portaled (#943): the `.hfse` wrapper re-scopes the cssString font rules
  // inside the portal's own shadow root, and ELEVATED_TOKEN_SCOPE re-scopes the
  // THEME TOKENS — those are emitted onto .hfse-explorer-container /
  // .hr-dialog-content, neither of which a detached portal host inherits, so
  // without it every var(--hfse-*) fell back to its light literal and the
  // popover painted white over a dark widget (#1246 review). React portals keep synthetic
  // events bubbling through the REACT tree, so the option rows' onMouseDown
  // and the field's delegated handlers behave exactly as in-tree. Until the
  // portal target and the first anchor measurement exist, render nothing — an
  // in-tree fallback frame would flash the trapped (#940) stacking.
  const listbox = whereListboxPortaled
    ? (listboxNode && listboxPortalTarget && listboxAnchor
        ? createPortal(<div className={`hfse ${ELEVATED_TOKEN_SCOPE}`}>{listboxNode}</div>, listboxPortalTarget)
        : null)
    : listboxNode

  if (sheet) {
    return (
      // rootRef stays on the stage root so the outside-close containment
      // (composedPath) keeps reading header + body taps as inside.
      <div className="hfse-sbp-where hfse-sbp-where-inline" ref={rootRef}>
        <button
          type="button"
          className={`hfse-sbp-field hfse-sbp-field-btn hfse-sbp-stage-header${hasApplied ? ' hfse-sbp-field-has-value' : ''}`}
          aria-expanded={sheetActive}
          onClick={onSheetActivate}
        >
          <span className="hfse-sbp-field-label">{labels.whereLabel}</span>
          <span className={`hfse-sbp-field-value ${hasApplied ? '' : 'hfse-sbp-field-placeholder'}`}>
            {appliedDisplay || labels.whereIdle}
          </span>
          {hasApplied && <FieldClear label="Clear destination" onClear={clearSelection} />}
        </button>
        {sheetActive && (
          <div className="hfse-sbp-stage-body">
            <h3 className="hfse-sbp-stage-question">Where to?</h3>
            {inputRow}
            {/* Module: "SUGGESTED DESTINATIONS" (uppercased by CSS) between
                the input and the list, only when there are destinations —
                whichever list is actually painted. Gating on filteredSections
                in destination mode read the hidden plain universe (#1246). */}
            {listboxVisible && (destinationMode ? destinationEntries.length > 0 : filteredSections.length > 0) && (
              <p className="hfse-sbp-suggestions-label">{labels.suggestedDestinations}</p>
            )}
            {listbox}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={`hfse-sbp-field hfse-sbp-where ${open ? 'hfse-sbp-field-active' : ''} ${inline ? 'hfse-sbp-where-inline' : ''} ${!open && hasApplied ? 'hfse-sbp-field-has-value' : ''}`}
      ref={rootRef}
      onMouseDown={inline ? undefined : onFieldMouseDown}
    >
      {/* inline (mobile sheet) and desktop instances can be mounted at once — the id must differ per role */}
      {/* Compact keeps the <label> (it is the input's accessible name) but
          drops it from view: sr-only class instead of the visible label row. */}
      <label className={compact ? 'hfse-sbp-sr-label' : 'hfse-sbp-field-label'} htmlFor={`hfse-sbp-where-${widgetId}${inline ? '-inline' : ''}`}>{labels.whereLabel}</label>
      {inputRow}
      {/* Reconciled onto the shared 28px clear (#870 QA): it used to be a 22px
          filled chip inside the input row. It now sits where the other three
          do — absolutely positioned against the field — so all four pills
          clear from the same spot. */}
      {!open && hasApplied && <FieldClear label="Clear destination" onClear={clearSelection} />}
      {listbox}
    </div>
  )
}
